import { Injectable, signal, computed } from '@angular/core';
import { PatientData, ImmunizationData } from '../../shared/models/patient.model';
import {
  ReferencedResources,
  FHIRBundleEntry,
  FHIRBundleLink,
  ConsultarRdaCompletoResponse,
} from '../../shared/models/fhir.models';

/**
 * Servicio de estado global del visor RDA.
 * Fuente única de verdad — usa Angular Signals para reactividad.
 *
 * Modo unificado (activo):
 *   rdaCompletoResponse → respuesta completa de POST /rda/consultar-rda-completo.
 *   Incluye entries de ambas fuentes + recursos referenciados + resumen.
 *   Los computed signals exponen los datos ya desagregados.
 */
@Injectable({ providedIn: 'root' })
export class RdaStateService {
  // ── Estado de carga ──────────────────────────────────────────────────────
  readonly loading = signal(false);
  readonly loadingImm = signal(false);
  readonly error = signal<string | null>(null);

  // ── Respuesta unificada del backend ──────────────────────────────────────
  /**
   * Respuesta completa del endpoint POST /rda/consultar-rda-completo.
   * El ApiResponseInterceptor ya desenvuelve el envelope { success, data, timestamp }.
   */
  readonly rdaCompletoResponse = signal<ConsultarRdaCompletoResponse | null>(null);

  /**
   * Snapshot de la primera página (resultado de consultar-rda-completo).
   * Se usa para restaurar el estado cuando el usuario retrocede hasta la página inicial.
   */
  readonly initialRdaResponse = signal<ConsultarRdaCompletoResponse | null>(null);

  // ── Datos procesados ─────────────────────────────────────────────────────
  readonly patientData = signal<PatientData | null>(null);
  readonly immunizations = signal<ImmunizationData[]>([]);
  readonly currentPatientId = signal<string>('');

  // ── Computed: datos derivados de la respuesta unificada ──────────────────

  readonly referencedResources = computed<ReferencedResources | null>(
    () => this.rdaCompletoResponse()?.referencedResources ?? null,
  );

  readonly allEntries = computed<FHIRBundleEntry[]>(
    () => this.rdaCompletoResponse()?.entriesBySource?.paciente ?? [],
  );

  readonly encuentrosEntries = computed<FHIRBundleEntry[]>(
    () => this.rdaCompletoResponse()?.entriesBySource?.encuentros ?? [],
  );

  readonly paginationLinks = computed<FHIRBundleLink[]>(
    () => this.rdaCompletoResponse()?.link ?? [],
  );

  readonly summary = computed(() => this.rdaCompletoResponse()?.summary ?? null);

  readonly rdaDetails = computed(() => this.rdaCompletoResponse()?.rdaDetails ?? null);

  // ── Contadores (computed) ────────────────────────────────────────────────
  readonly contadorVacunas = computed(() => this.immunizations().length);

  readonly contadorEncuentros = computed(() => this.encuentrosEntries().length);

  readonly contadorDocumentos = computed(() => this.allEntries().length);

  readonly contadorTotalCompositions = computed(
    () =>
      this.summary()?.totalCompositions ??
      this.allEntries().length + this.encuentrosEntries().length,
  );

  // ── Aliases semánticos (computed) ────────────────────────────────────────

  /** Compositions de antecedentes manifestados por el paciente */
  readonly documentosHistorial = computed(() => this.allEntries());

  /** Compositions de encuentros clínicos */
  readonly documentosEncuentros = computed(() => this.encuentrosEntries());

  // ── Historial de navegación (paginación cliente) ─────────────────────────
  /**
   * Stack de URLs visitadas durante la paginación.
   * Cada vez que el usuario avanza a una nueva página, la URL de la página
   * actual se apila aquí. Al pulsar "Anterior" se hace pop y se navega a ella.
   * El servidor IHCE no siempre devuelve un link `prev`, por lo que este stack
   * permite retroceder de forma confiable en el cliente.
   */
  readonly pageHistory = signal<string[]>([]);

  /**
   * URL de la página actualmente visible.
   * Se actualiza cada vez que se navega a una nueva página.
   * Permite saber qué URL apilar en el historial al avanzar.
   */
  readonly currentPageUrl = signal<string | null>(null);

  // ── Reset ────────────────────────────────────────────────────────────────
  reset(): void {
    this.rdaCompletoResponse.set(null);
    this.initialRdaResponse.set(null);
    this.patientData.set(null);
    this.immunizations.set([]);
    this.currentPatientId.set('');
    this.error.set(null);
    this.pageHistory.set([]);
    this.currentPageUrl.set(null);
  }
}
